import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { DealBasicTab } from "./DealEditor/DealBasicTab";
import { DealRevenueTab } from "./DealEditor/DealRevenueTab";
import { DealInvestmentTab } from "./DealEditor/DealInvestmentTab";
import { DealCostsTab } from "./DealEditor/DealCostsTab";
import { DealFinancingTab } from "./DealEditor/DealFinancingTab";
import { DealExitTab } from "./DealEditor/DealExitTab";
import { DealEditorProps } from "./DealEditor/types";
import { DealHeader } from "./DealEditor/DealHeader";
import { DealTabs } from "./DealEditor/DealTabs";
import * as propertyService from "@/lib/services/propertyService";

import { defaultKpis } from "./data";
import DealPayroll from "./DealEditor/PayrollTab";

interface PropertyTemplateData {
  id: string;
  slug: string;
  dealName: string;
  city: string;
  country: string;
  fullAddress: string;
  photoUrl: string;
  propertyType: string;
  featured: boolean;
  stars: number;
  currency: string;
  gfaSqm: number;
  purchasePrice: number;
  roomsTotal: number;
  facilities: string[];
  showInTemplates: boolean;
  showInGallery: boolean;
  description: string;
  investmentThesis: string;
  kpis?: any;
  createdAt: string;
  updatedAt: string;
}

export default function DealEditor({
  mode = "create",
  dealId: propDealId,
  onSave,
  onCancel,
}: DealEditorProps) {
  const navigate = useNavigate();
  const { id: urlDealId } = useParams<{ id: string }>();
  const dealId = propDealId || urlDealId;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("basic");

  const [property, setProperty] = useState<PropertyTemplateData>({
    id: crypto.randomUUID(),
    slug: "",
    dealName: "",
    city: "",
    country: "",
    fullAddress: "",
    photoUrl: "",
    propertyType: "Boutique Hotel",
    featured: false,
    stars: 4,
    currency: "EUR",
    gfaSqm: 0,
    purchasePrice: 0,
    roomsTotal: 0,
    facilities: [],
    showInTemplates: true,
    showInGallery: true,
    description: "",
    investmentThesis: "",
    kpis: defaultKpis,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  });

  useEffect(() => {
    if (mode === "edit" && dealId) {
      loadProperty(dealId);
    }
  }, [mode, dealId]);

  const loadProperty = async (id: string) => {
    try {
      setLoading(true);
      const propertyData = await propertyService.getPropertyById(id);

      if (!propertyData) {
        toast.error("Property not found");
        navigate("/admin/deals");
        return;
      }

      const roomMix = propertyData.kpis?.roomMix || [];

      const normalizedRoomMix = roomMix.map((r: any) => ({
        id: r.id || crypto.randomUUID(),
        name: r.name || r.type || "",
        rooms: Number(r.rooms ?? r.count ?? 0),
        adrWeight: Number(r.adrWeight ?? 100),
      }));

      const totalRooms = normalizedRoomMix.reduce(
        (sum: number, r: any) => sum + r.rooms,
        0,
      );

      setProperty({
        id: propertyData.id,
        slug: propertyData.slug || "",
        dealName: propertyData.dealName || "",
        city: propertyData.city || "",
        country: propertyData.country || "",
        fullAddress: propertyData.fullAddress || "",
        photoUrl: propertyData.photoUrl || "",
        propertyType: propertyData.propertyType || "Boutique Hotel",
        featured: propertyData.featured || false,
        stars: propertyData.stars || 4,
        currency: propertyData.currency || "EUR",
        gfaSqm: propertyData.gfaSqm || 0,
        purchasePrice: propertyData.purchasePrice || 0,
        roomsTotal: totalRooms || propertyData.roomsTotal || 0,
        facilities: propertyData.facilities || [],
        showInTemplates: propertyData.showInTemplates ?? true,
        showInGallery: propertyData.showInGallery ?? true,
        description: propertyData.description || "",
        investmentThesis: propertyData.investment_thesis || "",
        kpis: {
          ...(propertyData.kpis || defaultKpis),
          roomMix: normalizedRoomMix,
          rooms: { total: totalRooms },
        },
        createdAt: propertyData.createdAt || new Date().toISOString(),
        updatedAt: propertyData.updatedAt || new Date().toISOString(),
      });
    } catch (error) {
      console.error("Failed to load property:", error);
      toast.error("Failed to load property data");
      navigate("/admin/deals");
    } finally {
      setLoading(false);
    }
  };

  const updateBasicData = (updates: Partial<PropertyTemplateData>) => {
    setProperty((prev) => ({ ...prev, ...updates }));
  };

  const updateKpis = (updates: any) => {
    setProperty((prev) => ({
      ...prev,
      kpis: { ...prev.kpis, ...updates },
    }));
  };

  const handleSave = async () => {
    if (!property.dealName.trim()) {
      toast.error("Property name is required");
      return;
    }
    if (!property.city.trim()) {
      toast.error("City is required");
      return;
    }
    if (!property.country.trim()) {
      toast.error("Country is required");
      return;
    }

    setSaving(true);

    try {
      const slug = property.dealName.toLowerCase().replace(/\s+/g, "-");

      const roomMix = property.kpis?.roomMix || [];
      const totalRooms = roomMix.reduce(
        (sum: number, r: any) => sum + (Number(r.rooms) || 0),
        0,
      );

      const currentPayroll = property.kpis?.payroll || {
        advanced: { roles: [] },
      };

      const propertyToSave: any = {
        id: property.id,
        slug,
        dealName: property.dealName,
        city: property.city,
        country: property.country,
        fullAddress: property.fullAddress,
        photoUrl: property.photoUrl,
        propertyType: property.propertyType,
        featured: property.featured,
        stars: property.stars as 1 | 2 | 3 | 4 | 5,
        currency: property.currency,
        gfaSqm: property.gfaSqm,
        purchasePrice: property.purchasePrice,
        rooms_total: totalRooms,
        total_rooms: totalRooms,
        facilities: property.facilities,
        showInTemplates: property.showInTemplates,
        showInGallery: property.showInGallery,
        description: property.description,
        investment_thesis: property.investmentThesis,
        kpis: {
          ...property.kpis,
          dealName: property.dealName,
          location: `${property.city}, ${property.country}`,
          dealLocation: property.country,
          address: property.fullAddress || property.city,
          fullAddress: property.fullAddress || property.city,
          photoUrl: property.photoUrl,
          photos: property.kpis?.photos || [],
          propertyType: property.propertyType,
          stars: property.stars,
          currency: property.currency,
          gfaSqm: property.gfaSqm,
          purchasePrice: property.purchasePrice,
          purchase: { price: property.purchasePrice },
          roomsTotal: totalRooms,
          rooms: { total: totalRooms },
          roomMix,
          facilities: property.facilities,
          normalizedFacilities: property.facilities,
          property: { facilities: property.facilities },
          amenities: {
            bar: property.facilities.includes("bar"),
            gym: property.facilities.includes("gym"),
            spa: property.facilities.includes("spa"),
            pool: property.facilities.includes("pool"),
            parking: property.facilities.includes("parking"),
            meetings:
              property.facilities.includes("conference") ||
              property.facilities.includes("meetings"),
            restaurant: property.facilities.includes("restaurant"),
            room_service: property.facilities.includes("roomservice"),
          },
          payroll: currentPayroll,
        },
        createdAt: property.createdAt,
        updatedAt: new Date().toISOString(),
      };
      Object.keys(propertyToSave).forEach((key) => {
        if (propertyToSave[key] === undefined) {
          delete propertyToSave[key];
        }
      });

      if (mode === "create") {
        await propertyService.createProperty(propertyToSave);
        toast.success(`Created "${property.dealName}"`);
        navigate("/admin/deals");
      } else {
        const savedProperty = await propertyService.updateProperty(
          dealId!,
          propertyToSave,
        );
        toast.success(`Updated "${property.dealName}"`);
        if (onSave) onSave(savedProperty);
      }
    } catch (error: any) {
      console.error("Save error:", error);
      toast.error(error?.message ?? "Failed to save property");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      navigate("/admin/deals");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="animate-spin h-8 w-8 border-4 border-slate-300 border-t-slate-600 rounded-full mx-auto mb-4"></div>
          <p className="text-slate-600">Loading property data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto">
      <DealHeader
        mode={mode}
        loading={saving}
        onSave={handleSave}
        onCancel={handleCancel}
      />

      <DealTabs activeTab={activeTab} onTabChange={setActiveTab}>
        <DealBasicTab
          formData={{
            name: property.dealName,
            city: property.city,
            country: property.country,
            propertyType: property.propertyType,
            stars: property.stars,
            purchasePrice: property.purchasePrice,
            currency: property.currency,
            gfaSqm: property.gfaSqm,
            roomsTotal: property.roomsTotal,
            description: property.description,
            investmentThesis: property.investmentThesis,
            photos: property.kpis?.photos || [],
            facilities: property.facilities,
            featured: property.featured,
            showInTemplates: property.showInTemplates,
            showInGallery: property.showInGallery,
            fullAddress: property.fullAddress,
            photoUrl: property.photoUrl,
          }}
          onFormDataChange={(updates) => {
            const backendUpdates: any = {};
            const kpisUpdates: any = {};

            if (updates.name !== undefined) {
              backendUpdates.dealName = updates.name;
              kpisUpdates.dealName = updates.name;
            }
            if (updates.city !== undefined) {
              backendUpdates.city = updates.city;
              kpisUpdates.address = updates.city;
              kpisUpdates.location = `${updates.city}, ${property.country}`;
            }
            if (updates.country !== undefined) {
              backendUpdates.country = updates.country;
              kpisUpdates.location = `${property.city}, ${updates.country}`;
              kpisUpdates.dealLocation = updates.country;
            }
            if (updates.propertyType !== undefined) {
              backendUpdates.propertyType = updates.propertyType;
              kpisUpdates.propertyType = updates.propertyType;
            }
            if (updates.stars !== undefined) {
              backendUpdates.stars = updates.stars;
              kpisUpdates.stars = updates.stars;
            }
            if (updates.purchasePrice !== undefined) {
              backendUpdates.purchasePrice = updates.purchasePrice;
              kpisUpdates.purchasePrice = updates.purchasePrice;
              kpisUpdates.purchase = {
                ...property.kpis?.purchase,
                price: updates.purchasePrice,
              };
            }
            if (updates.currency !== undefined) {
              backendUpdates.currency = updates.currency;
              kpisUpdates.currency = updates.currency;
            }
            if (updates.gfaSqm !== undefined) {
              backendUpdates.gfaSqm = updates.gfaSqm;
              kpisUpdates.gfaSqm = updates.gfaSqm;
            }
            if (updates.roomsTotal !== undefined) {
              backendUpdates.roomsTotal = updates.roomsTotal;
              kpisUpdates.roomsTotal = updates.roomsTotal;
              kpisUpdates.rooms = { total: updates.roomsTotal };
            }
            if (updates.description !== undefined) {
              backendUpdates.description = updates.description;
            }
            if (updates.investmentThesis !== undefined) {
              backendUpdates.investmentThesis = updates.investmentThesis;
              backendUpdates.investment_thesis = updates.investmentThesis;
              kpisUpdates.investmentThesis = updates.investmentThesis;
            }
            if (updates.facilities !== undefined) {
              backendUpdates.facilities = updates.facilities;
              kpisUpdates.facilities = updates.facilities;
              kpisUpdates.normalizedFacilities = updates.facilities;
              kpisUpdates.property = { facilities: updates.facilities };
              kpisUpdates.amenities = {
                bar: updates.facilities.includes("bar"),
                gym: updates.facilities.includes("gym"),
                spa: updates.facilities.includes("spa"),
                pool: updates.facilities.includes("pool"),
                parking: updates.facilities.includes("parking"),
                meetings:
                  updates.facilities.includes("conference") ||
                  updates.facilities.includes("meetings"),
                restaurant: updates.facilities.includes("restaurant"),
                room_service: updates.facilities.includes("roomservice"),
              };
            }
            if (updates.featured !== undefined) {
              backendUpdates.featured = updates.featured;
            }
            if (updates.showInTemplates !== undefined) {
              backendUpdates.showInTemplates = updates.showInTemplates;
            }
            if (updates.showInGallery !== undefined) {
              backendUpdates.showInGallery = updates.showInGallery;
            }
            if (updates.fullAddress !== undefined) {
              backendUpdates.fullAddress = updates.fullAddress;
              kpisUpdates.fullAddress = updates.fullAddress;
              kpisUpdates.address = updates.fullAddress;
            }
            if (updates.photoUrl !== undefined) {
              backendUpdates.photoUrl = updates.photoUrl;
              kpisUpdates.photoUrl = updates.photoUrl;
            }
            if (updates.photos !== undefined) {
              kpisUpdates.photos = updates.photos;
              if (!updates.photoUrl && updates.photos.length > 0) {
                backendUpdates.photoUrl = updates.photos[0];
                kpisUpdates.photoUrl = updates.photos[0];
              }
            }

            updateBasicData(backendUpdates);
            if (Object.keys(kpisUpdates).length > 0) {
              updateKpis(kpisUpdates);
            }
          }}
          kpis={property.kpis}
          onKpisChange={updateKpis}
          dealId={dealId}
        />
        <DealRevenueTab
          kpis={property.kpis}
          onKpisChange={updateKpis}
          currency={property.currency}
        />
        <DealCostsTab kpis={property.kpis} onKpisChange={updateKpis} />
        <DealPayroll
          dealId={dealId}
          rooms={property.roomsTotal}
          currency={property.currency}
        />
        <DealInvestmentTab
          kpis={property.kpis}
          onKpisChange={updateKpis}
          currency={property.currency}
        />
        <DealFinancingTab kpis={property.kpis} onKpisChange={updateKpis} />
        <DealExitTab kpis={property.kpis} onKpisChange={updateKpis} />
      </DealTabs>
    </div>
  );
}
